import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calculator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [calculationResults, setCalculationResults] = useState(null);
  const [submittingLead, setSubmittingLead] = useState(false);
  
  const [formData, setFormData] = useState({
    employees_count: '',
    hourly_wage: '',
    hours_per_week: 40,
    turnover_rate_pct: 0.30,
    forge_bill_rate: ''
  });

  const [benchmarks, setBenchmarks] = useState({
    payroll_tax_pct: 0.10,
    benefits_pct: 0.25,
    workers_comp_pct: 0.02,
    recruiting_cost: 3000,
    onboarding_training_cost: 1500,
    vacancy_days: 14,
    vacancy_productivity_loss_pct: 0.50,
    rampup_days: 30,
    rampup_productivity_loss_pct: 0.30,
    weeks_per_year: 52
  });

  const [leadData, setLeadData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone_number: '',
    sales_person_email: ''
  });

  useEffect(() => {
    fetchBenchmarks();
  }, []);

  const fetchBenchmarks = async () => {
    try {
      const response = await axios.get(`${API}/benchmarks`);
      setBenchmarks(response.data);
    } catch (error) {
      console.error('Failed to fetch benchmarks:', error);
    }
  };

  const handleBenchmarkChange = (field, value) => {
    setBenchmarks(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveBenchmarks = async () => {
    try {
      await axios.put(`${API}/benchmarks`, benchmarks);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save benchmarks:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.employees_count || !formData.hourly_wage || !formData.forge_bill_rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const calculationInput = {
        employees_count: parseInt(formData.employees_count),
        hourly_wage: parseFloat(formData.hourly_wage),
        hours_per_week: parseFloat(formData.hours_per_week),
        turnover_rate_pct: parseFloat(formData.turnover_rate_pct),
        forge_bill_rate: parseFloat(formData.forge_bill_rate)
      };

      const response = await axios.post(`${API}/calculate`, calculationInput);
      
      // Store calculation results and show lead form
      setCalculationResults(response.data);
      setShowLeadForm(true);
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmittingLead(true);

    try {
      const leadPayload = {
        ...leadData,
        calculation_data: calculationResults
      };

      await axios.post(`${API}/leads`, leadPayload);
      toast.success('Thank you! Redirecting to your results...');
      
      // Navigate to results page with calculation data and lead data
      setTimeout(() => {
        navigate('/results', { 
          state: { 
            results: calculationResults,
            leadData: leadData
          } 
        });
      }, 1000);
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmittingLead(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)' }}>
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', 
        padding: '20px 15px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_25403674-ae6e-49e1-bb0a-d271228220d4/artifacts/1u0fumrd_Logo%20with%20Text%20%28RED%20LOGO%20WHITE%20FONT%29.png" 
              alt="Forge Industrial Staffing"
              style={{ height: '60px', maxWidth: '200px', objectFit: 'contain' }}
            />
          </div>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'transparent',
              color: 'white',
              border: '2px solid rgba(255,255,255,0.3)',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontWeight: '500'
            }}
            onMouseOver={(e) => {
              e.target.style.borderColor = '#C41E3A';
              e.target.style.background = 'rgba(196, 30, 58, 0.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.borderColor = 'rgba(255,255,255,0.3)';
              e.target.style.background = 'transparent';
            }}
            data-testid="admin-link-btn"
          >
            Admin
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px' }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '40px', padding: '0 15px' }}>
          <h1 style={{ 
            fontSize: 'clamp(24px, 5vw, 48px)', 
            fontWeight: '700', 
            color: '#1a1a1a',
            marginBottom: '16px',
            lineHeight: '1.2'
          }}>
            Staffing Cost Comparison Calculator
          </h1>
          <p style={{ 
            fontSize: 'clamp(14px, 3vw, 18px)', 
            color: '#6b7280',
            maxWidth: '600px',
            margin: '0 auto',
            padding: '0 10px'
          }}>
            Discover how much you can save by partnering with Forge Industrial Staffing
          </p>
        </div>

        {/* Calculator Form with Tabs */}
        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="basic" data-testid="basic-inputs-tab">Basic Settings</TabsTrigger>
              <TabsTrigger value="advanced" data-testid="advanced-settings-tab">Advanced Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <form onSubmit={handleSubmit} data-testid="calculator-form">
                <div style={{ display: 'grid', gap: '28px' }}>
              {/* Number of Employees */}
              <div>
                <label htmlFor="employees_count">
                  Number of Employees Needed <span style={{ color: '#C41E3A' }}>*</span>
                </label>
                <input
                  type="number"
                  id="employees_count"
                  name="employees_count"
                  value={formData.employees_count}
                  onChange={handleInputChange}
                  placeholder="e.g., 10"
                  min="1"
                  required
                  data-testid="employees-count-input"
                />
              </div>

              {/* Hourly Wage */}
              <div>
                <label htmlFor="hourly_wage">
                  Hourly Wage ($/hr) <span style={{ color: '#C41E3A' }}>*</span>
                </label>
                <input
                  type="number"
                  id="hourly_wage"
                  name="hourly_wage"
                  value={formData.hourly_wage}
                  onChange={handleInputChange}
                  placeholder="e.g., 25.00"
                  step="0.01"
                  min="0"
                  required
                  data-testid="hourly-wage-input"
                />
              </div>

              {/* Hours Per Week */}
              <div>
                <label htmlFor="hours_per_week">
                  Hours Per Week
                </label>
                <input
                  type="number"
                  id="hours_per_week"
                  name="hours_per_week"
                  value={formData.hours_per_week}
                  onChange={handleInputChange}
                  placeholder="40"
                  min="1"
                  max="168"
                  data-testid="hours-per-week-input"
                />
              </div>

              {/* Forge Bill Rate */}
              <div>
                <label htmlFor="forge_bill_rate">
                  Forge Bill Rate ($/hr) <span style={{ color: '#C41E3A' }}>*</span>
                </label>
                <input
                  type="number"
                  id="forge_bill_rate"
                  name="forge_bill_rate"
                  value={formData.forge_bill_rate}
                  onChange={handleInputChange}
                  placeholder="e.g., 32.00"
                  step="0.01"
                  min="0"
                  required
                  data-testid="forge-bill-rate-input"
                />
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>
                  Enter the Forge staffing rate for this position
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn-forge"
                disabled={loading}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  opacity: loading ? 0.7 : 1
                }}
                data-testid="calculate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Calculating...
                  </>
                ) : (
                  'Calculate Savings'
                )}
              </button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="advanced">
          <div style={{ display: 'grid', gap: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1a1a1a' }}>
                Customize Calculation Benchmarks
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Adjust these default values to match your specific industry or business needs.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {/* Turnover Rate */}
              <div>
                <Label htmlFor="turnover_rate_pct">Turnover Rate (%)</Label>
                <Input
                  id="turnover_rate_pct"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.turnover_rate_pct * 100}
                  onChange={(e) => setFormData(prev => ({ ...prev, turnover_rate_pct: parseFloat(e.target.value) / 100 }))}
                  data-testid="turnover-rate-input"
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Annual turnover rate (default: 30%)
                </p>
              </div>

              <div>
                <Label htmlFor="payroll_tax_pct">Payroll Tax (%)</Label>
                <Input
                  id="payroll_tax_pct"
                  type="number"
                  step="0.01"
                  value={Math.round(benchmarks.payroll_tax_pct * 10000) / 100}
                  onChange={(e) => handleBenchmarkChange('payroll_tax_pct', parseFloat(e.target.value) / 100)}
                  data-testid="payroll-tax-input"
                />
              </div>

              <div>
                <Label htmlFor="benefits_pct">Benefits Load (%)</Label>
                <Input
                  id="benefits_pct"
                  type="number"
                  step="0.01"
                  value={Math.round(benchmarks.benefits_pct * 10000) / 100}
                  onChange={(e) => handleBenchmarkChange('benefits_pct', parseFloat(e.target.value) / 100)}
                  data-testid="benefits-input"
                />
              </div>

              <div>
                <Label htmlFor="workers_comp_pct">Workers' Comp (%)</Label>
                <Input
                  id="workers_comp_pct"
                  type="number"
                  step="0.01"
                  value={Math.round(benchmarks.workers_comp_pct * 10000) / 100}
                  onChange={(e) => handleBenchmarkChange('workers_comp_pct', parseFloat(e.target.value) / 100)}
                  data-testid="workers-comp-input"
                />
              </div>

              <div>
                <Label htmlFor="recruiting_cost">Recruiting Cost ($)</Label>
                <Input
                  id="recruiting_cost"
                  type="number"
                  value={benchmarks.recruiting_cost}
                  onChange={(e) => handleBenchmarkChange('recruiting_cost', parseFloat(e.target.value))}
                  data-testid="recruiting-cost-input"
                />
              </div>

              <div>
                <Label htmlFor="onboarding_training_cost">Onboarding/Training ($)</Label>
                <Input
                  id="onboarding_training_cost"
                  type="number"
                  value={benchmarks.onboarding_training_cost}
                  onChange={(e) => handleBenchmarkChange('onboarding_training_cost', parseFloat(e.target.value))}
                  data-testid="onboarding-cost-input"
                />
              </div>

              <div>
                <Label htmlFor="vacancy_days">Vacancy Days</Label>
                <Input
                  id="vacancy_days"
                  type="number"
                  value={benchmarks.vacancy_days}
                  onChange={(e) => handleBenchmarkChange('vacancy_days', parseInt(e.target.value))}
                  data-testid="vacancy-days-input"
                />
              </div>

              <div>
                <Label htmlFor="vacancy_productivity_loss_pct">Vacancy Loss (%)</Label>
                <Input
                  id="vacancy_productivity_loss_pct"
                  type="number"
                  step="0.01"
                  value={Math.round(benchmarks.vacancy_productivity_loss_pct * 10000) / 100}
                  onChange={(e) => handleBenchmarkChange('vacancy_productivity_loss_pct', parseFloat(e.target.value) / 100)}
                  data-testid="vacancy-loss-input"
                />
              </div>

              <div>
                <Label htmlFor="rampup_days">Ramp-up Days</Label>
                <Input
                  id="rampup_days"
                  type="number"
                  value={benchmarks.rampup_days}
                  onChange={(e) => handleBenchmarkChange('rampup_days', parseInt(e.target.value))}
                  data-testid="rampup-days-input"
                />
              </div>

              <div>
                <Label htmlFor="rampup_productivity_loss_pct">Ramp-up Loss (%)</Label>
                <Input
                  id="rampup_productivity_loss_pct"
                  type="number"
                  step="0.01"
                  value={Math.round(benchmarks.rampup_productivity_loss_pct * 10000) / 100}
                  onChange={(e) => handleBenchmarkChange('rampup_productivity_loss_pct', parseFloat(e.target.value) / 100)}
                  data-testid="rampup-loss-input"
                />
              </div>

              <div>
                <Label htmlFor="weeks_per_year">Weeks Per Year</Label>
                <Input
                  id="weeks_per_year"
                  type="number"
                  value={benchmarks.weeks_per_year}
                  onChange={(e) => handleBenchmarkChange('weeks_per_year', parseInt(e.target.value))}
                  data-testid="weeks-per-year-input"
                />
              </div>
            </div>

            <Button
              onClick={saveBenchmarks}
              className="bg-[#C41E3A] hover:bg-[#8B0000]"
              data-testid="save-benchmarks-btn"
            >
              Save Settings
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>

        {/* Info Section */}
        <div style={{ 
          marginTop: '40px', 
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.05) 0%, rgba(196, 30, 58, 0.02) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(196, 30, 58, 0.1)'
        }}>
          <h3 style={{ color: '#C41E3A', marginBottom: '12px', fontSize: '18px' }}>How It Works</h3>
          <p style={{ color: '#6b7280', fontSize: '15px', lineHeight: '1.6', marginBottom: '12px' }}>
            Our calculator compares the true cost of direct hiring (including recruiting, training, benefits, turnover costs, and productivity losses) against Forge Industrial Staffing's all-inclusive rate. See your potential savings instantly.
          </p>
          <p style={{ color: '#6b7280', fontSize: '13px', lineHeight: '1.6', fontStyle: 'italic' }}>
            <strong>Please note:</strong> The calculations provided are estimates based on industry benchmarks and the information you provide. Actual costs and savings may vary depending on your specific circumstances, location, industry, and business practices. For a detailed analysis tailored to your organization, please contact our team.
          </p>
        </div>
      </div>

      {/* Lead Capture Dialog */}
      <Dialog open={showLeadForm} onOpenChange={setShowLeadForm}>
        <DialogContent className="sm:max-w-[500px]" data-testid="lead-capture-dialog">
          <DialogHeader>
            <DialogTitle>Almost There! Get Your Detailed Report</DialogTitle>
            <DialogDescription>
              Enter your information to view your personalized cost savings analysis.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit}>
            <div style={{ display: 'grid', gap: '16px', padding: '20px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={leadData.first_name}
                    onChange={(e) => setLeadData({ ...leadData, first_name: e.target.value })}
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={leadData.last_name}
                    onChange={(e) => setLeadData({ ...leadData, last_name: e.target.value })}
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={leadData.company_name}
                  onChange={(e) => setLeadData({ ...leadData, company_name: e.target.value })}
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={leadData.email}
                  onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="phone_number">Phone Number *</Label>
                <Input
                  id="phone_number"
                  type="tel"
                  value={leadData.phone_number}
                  onChange={(e) => setLeadData({ ...leadData, phone_number: e.target.value })}
                  required
                  data-testid="phone-number-input"
                />
              </div>

              <div>
                <Label htmlFor="sales_person_email">Sales Person Email (Optional)</Label>
                <Input
                  id="sales_person_email"
                  type="email"
                  value={leadData.sales_person_email}
                  onChange={(e) => setLeadData({ ...leadData, sales_person_email: e.target.value })}
                  placeholder="Leave blank if not applicable"
                  data-testid="sales-person-email-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowLeadForm(false)}
                data-testid="cancel-lead-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submittingLead}
                className="bg-[#C41E3A] hover:bg-[#8B0000]"
                data-testid="submit-lead-btn"
              >
                {submittingLead ? 'Submitting...' : 'View My Results'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calculator;
